import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { clientKeys } from '@/api/queries/clients';
import { articleKeys } from '@/api/queries/articles';
import { supplierKeys } from '@/api/queries/accounting-suppliers';
import { accountingKeys } from '@/api/queries/accounting';
import { auxiliaryKeys } from '@/api/queries/accounting-auxiliaries';
import { accountingOpsKeys } from '@/api/queries/accounting-ops';
import { hrKeys } from '@/api/queries/hr';
import { crmKeys } from '@/api/queries/crm';
import type { SignupAntiAbuseFields } from '@/api/queries/public-offer';

// ─────────────────────────────────────────────────────────────
// Types — inscription self-serve (routes publiques)
// Contrats serveur: packages/api/src/routes/public/index.ts
//                   packages/api/src/services/onboarding/signup.service.ts
// ─────────────────────────────────────────────────────────────

/** Régimes d'imposition du tenant — mêmes valeurs que Tenant.taxRegime. */
export type TaxRegime = 'RNI' | 'RSI' | 'MICRO';

export interface SignupInput extends SignupAntiAbuseFields {
  companyName: string;
  ifu: string;
  /** Code BUSINESS_TYPES de @efacture/core (DETAIL, PHARMACIE, …). */
  businessType: string;
  /** Country of the company, a code of GET /public/jurisdictions (#181); Burkina Faso when absent. */
  jurisdiction?: string;
  /** Asked only where the country has one (Burkina Faso). */
  taxRegime?: TaxRegime;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  /** The one-time code of a standalone site node's first run. */
  firstRunCode?: string;
}

export interface SignupResult {
  tenant: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    subscriptionStatus: string;
    trialEndsAt: string;
    taxRegime: string;
  };
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    tenantId: string;
  };
  /** false si l'e-mail de vérification n'a pas pu partir (renvoi possible). */
  verificationEmailSent: boolean;
  /** false when the account can sign in at once (desktop first run). */
  emailVerificationRequired: boolean;
}

export interface VerifyEmailResult {
  email: string;
  verifiedAt: string;
  message: string;
}

// ─────────────────────────────────────────────────────────────
// Types — assistant d'onboarding et imports de migration
// Contrats serveur: packages/api/src/routes/onboarding/index.ts
//                   packages/api/src/services/onboarding/*.ts
// ─────────────────────────────────────────────────────────────

export interface OnboardingStep {
  key: string;
  label: string;
  done: boolean;
  /** Étape non bloquante pour la complétion de l'assistant. */
  optional: boolean;
}

export interface OnboardingState {
  steps: OnboardingStep[];
  completedAt: string | null;
}

/** HR history imports whose rows carry their batch and can be cancelled. */
export type HrImportKind = 'leave-balances' | 'payroll-history';

/** CRM file imports (#539), undone by batch like the entity imports. */
export type CrmImportKind = 'contacts' | 'leads' | 'opportunities' | 'crm-activities';

export type ImportEntity =
  | CrmImportKind
  | 'clients'
  | 'articles'
  | 'suppliers'
  | 'employees'
  | 'chart-accounts'
  | HrImportKind;

/**
 * Endpoint of each CSV import; the HR imports live under the HR module and
 * its plan gate, the sub-account import under the accounting module.
 */
const IMPORT_PATHS: Record<ImportEntity, string> = {
  clients: '/onboarding/import/clients',
  articles: '/onboarding/import/articles',
  suppliers: '/onboarding/import/suppliers',
  employees: '/hr/employees/import',
  contacts: '/crm/imports/contacts',
  leads: '/crm/imports/leads',
  opportunities: '/crm/imports/opportunities',
  'crm-activities': '/crm/imports/activities',
  'chart-accounts': '/accounting/accounts/import',
  'leave-balances': '/hr/imports/leave-balances',
  'payroll-history': '/hr/imports/payroll-history',
};

/** One HR import batch whose rows are still present. */
export interface HrImportBatch {
  batchId: string;
  importedAt: string;
  fileName: string | null;
  userId: string | null;
  /** Payroll runs (payroll history) or opening balances (leave) of the batch. */
  recordCount: number;
  /** Payroll history: months « AAAA-MM », ascending. */
  months: string[];
  /** Leave balances: years, ascending. */
  years: number[];
}

export interface CancelHrImportResult {
  batchId: string;
  kind: HrImportKind;
  removed: number;
}

const hrImportBatchKeys = {
  all: ['hr', 'import-batches'] as const,
  list: (kind: HrImportKind) => [...hrImportBatchKeys.all, kind] as const,
};

/** Client, article, supplier, employee and sub-account imports, undone by batch. */
export type EntityImportKind =
  | CrmImportKind
  | 'clients'
  | 'articles'
  | 'suppliers'
  | 'employees'
  | 'chart-accounts';

/** Batch list of each kind; cancellation is `${path}/:batchId/cancel`. */
const ENTITY_IMPORT_BATCH_PATHS: Record<EntityImportKind, string> = {
  clients: '/onboarding/import/clients/batches',
  articles: '/onboarding/import/articles/batches',
  suppliers: '/onboarding/import/suppliers/batches',
  employees: '/hr/imports/employees/batches',
  contacts: '/crm/imports/contacts/batches',
  leads: '/crm/imports/leads/batches',
  opportunities: '/crm/imports/opportunities/batches',
  'crm-activities': '/crm/imports/activities/batches',
  'chart-accounts': '/accounting/accounts/import/batches',
};

/** One import batch whose records are still present. */
export interface EntityImportBatch {
  batchId: string;
  importedAt: string;
  fileName: string | null;
  userId: string | null;
  /** Records of the batch still present. */
  recordCount: number;
}

export interface CancelEntityImportResult {
  batchId: string;
  kind: EntityImportKind;
  removed: number;
  /** Clients, suppliers, employees: their auxiliary accounts removed. */
  auxiliaryAccountsRemoved?: number;
}

/** A record of the batch and what uses it, sent with a 409 IMPORT_UTILISE. */
export interface ImportBatchBlocker {
  record: string;
  usedBy: string;
}

const entityImportBatchKeys = {
  all: ['onboarding', 'import-batches'] as const,
  list: (kind: EntityImportKind) => [...entityImportBatchKeys.all, kind] as const,
};

function isCrmImportKind(entity: string): entity is CrmImportKind {
  return entity === 'contacts' || entity === 'leads' || entity === 'opportunities' || entity === 'crm-activities';
}

function isEntityImportKind(entity: ImportEntity): entity is EntityImportKind {
  return entity in ENTITY_IMPORT_BATCH_PATHS;
}

export interface CsvImportPayload {
  /** The file: its bytes in base64 (encoding "base64"), or plain CSV text. */
  content: string;
  encoding?: 'text' | 'base64';
  /** "csv" (default) or "xlsx". */
  format?: 'csv' | 'xlsx';
  /** Simulation: the server checks the file and writes only its audit row. */
  dryRun?: boolean;
  /** Séparateur de colonnes — défaut serveur ";" (exports BF usuels). */
  separator?: string;
  /** Lignes d'en-tête à ignorer — défaut serveur 1. */
  skipRows?: number;
  /** Mapping nom de champ → index de colonne (base 0). */
  columnMap: Record<string, number>;
  /** true = importer les lignes valides malgré des rejets. Défaut false (tout-ou-rien). */
  partial?: boolean;
  /** Decimal separator of the amounts (articles, employees) — server default ",". */
  decimalSeparator?: ',' | '.';
  /** Name of the uploaded file, recorded in the import's audit row. */
  fileName?: string;
  /** CRM imports: the source system that owns the external keys of the file. */
  source?: string;
}

export interface RejectedLine {
  /** Numéro de ligne du fichier (1-based, en-têtes comprises). */
  line: number;
  reason: string;
}

export interface ImportReport {
  imported: number;
  rejected: RejectedLine[];
  /** Lignes de données lues (hors en-têtes et lignes vides). */
  total: number;
  /**
   * Client, article, supplier, employee and HR history imports: the batch the
   * created rows carry, null when nothing was written.
   */
  batchId?: string | null;
  /**
   * Payroll history: one line per imported month whose CNSS branches could
   * not be identified (« branches à compléter » in the DRAS).
   */
  warnings?: string[];
  /** Client, article, supplier, employee imports: rows already present, unchanged. */
  unchanged?: number;
  /**
   * Client and supplier imports: the unchanged rows found by their name
   * alone (a client without code nor IFU, a supplier without IFU), absent
   * when there are none.
   */
  matchedByName?: Array<{ line: number; label: string }>;
  /** True for a simulation: `imported` is what the import would create. */
  dryRun?: boolean;
  /** The earlier import of the same file whose rows remain. */
  previousImport?: {
    batchId: string;
    importedAt: string;
    fileName: string | null;
    created: number;
  } | null;
}

export interface OpeningBalancePayload extends Omit<CsvImportPayload, 'partial'> {
  /** Date de l'écriture d'à-nouveaux (YYYY-MM-DD, dans l'exercice visé). */
  date: string;
  /** Libellé de l'écriture — défaut serveur « Balance d'ouverture {exercice} ». */
  label?: string;
  /** Séparateur décimal des montants — défaut serveur ",". */
  decimalSeparator?: ',' | '.';
}

export interface CancelImportedOpeningPayload {
  /** Exercise whose imported à-nouveau is cancelled. */
  year: number;
  /** Reason recorded in the audit log. */
  reason: string;
}

export interface CancelImportedOpeningResult {
  exerciseYear: number;
  cancelledEntry: { id: string; entryNumber: string | null };
  reversal: { id: string; entryNumber: string | null };
}

export interface OpeningBalanceReport extends ImportReport {
  /** Lignes à solde nul ignorées (visibles, jamais silencieuses). */
  ignoredZeroLines: number;
  entry: {
    id: string;
    entryNumber: string | null;
    totalDebit: number;
    totalCredit: number;
  } | null;
}

/** CLIENT: encours clients (411… and 419…) · SUPPLIER: encours fournisseurs (401… and 409…). */
export type OpenItemsSide = 'CLIENT' | 'SUPPLIER';

export interface OpenItemsPayload extends Omit<CsvImportPayload, 'partial'> {
  /** Exercise whose imported opening balance the open items detail. */
  year: number;
  side: OpenItemsSide;
  /** Decimal separator of the amounts — server default ",". */
  decimalSeparator?: ',' | '.';
}

export interface OpenItemsCollective {
  accountNumber: string;
  itemCount: number;
  /** Signed file total (debit positive). */
  fileBalance: number;
  /** Signed balance of the account's lines without third party in the opening. */
  openingBalance: number;
}

export interface OpenItemsReport extends ImportReport {
  side: OpenItemsSide;
  exerciseYear: number;
  collectives: OpenItemsCollective[];
  entry: {
    id: string;
    entryNumber: string | null;
    sourceId: string | null;
    totalDebit: number;
    totalCredit: number;
  } | null;
}

export interface CancelImportedOpenItemsPayload {
  year: number;
  side: OpenItemsSide;
  /** Reason recorded in the audit log. */
  reason: string;
}

export interface CancelImportedOpenItemsResult {
  exerciseYear: number;
  side: OpenItemsSide;
  cancelledEntry: { id: string; entryNumber: string | null };
  reversal: { id: string; entryNumber: string | null };
}

// ─────────────────────────────────────────────────────────────
// Clés de requête
// ─────────────────────────────────────────────────────────────

export const onboardingKeys = {
  all: ['onboarding'] as const,
  state: () => [...onboardingKeys.all, 'state'] as const,
};

// ─────────────────────────────────────────────────────────────
// Requêtes / mutations — routes publiques (sans authentification)
// ─────────────────────────────────────────────────────────────

/**
 * Self-serve signup — POST /api/public/signup
 * Creates the tenant on the launch offer (SUITE plan, TRIALING) plus its
 * admin, then sends the verification e-mail (best effort). The free period
 * length is the server's; GET /public/offer states it to the page.
 */
export function useSignup() {
  return useMutation({
    mutationFn: (input: SignupInput) =>
      apiClient.post<SignupResult>('/public/signup', input),
  });
}

/**
 * E-mail verification — POST /api/public/verify-email (token in the
 * body, never the URL, so it stays out of access logs and traces).
 * The token is SINGLE-USE server-side: this mutation must never be
 * retried automatically (a retry gets 400 TOKEN_DEJA_UTILISE).
 */
export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) =>
      apiClient.post<VerifyEmailResult>('/public/verify-email', { token }),
    retry: false,
  });
}

/**
 * Renvoi de l'e-mail de vérification — POST /api/public/resend-verification
 * Réponse toujours générique (pas d'énumération d'e-mails).
 */
export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) =>
      apiClient.post<{ message: string }>('/public/resend-verification', {
        email,
      }),
  });
}

// ─────────────────────────────────────────────────────────────
// Requêtes / mutations — assistant d'onboarding (authentifié)
// ─────────────────────────────────────────────────────────────

/**
 * État des étapes de l'assistant — GET /api/onboarding/state
 * Étapes calculées depuis les données réelles du tenant.
 */
export function useOnboardingState(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: onboardingKeys.state(),
    queryFn: () => apiClient.get<OnboardingState>('/onboarding/state'),
    enabled: options.enabled ?? true,
  });
}

/**
 * Fige Tenant.onboardingCompletedAt — POST /api/onboarding/complete
 * (idempotent ; permission tenant:settings côté serveur).
 */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.post<{ completedAt: string }>('/onboarding/complete'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.state() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations — imports de migration (CSV + columnMap)
// ─────────────────────────────────────────────────────────────

/**
 * File import of clients, articles, suppliers, employees, HR history and
 * chart sub-accounts — POST /api/onboarding/import/{clients|articles|suppliers},
 * POST /api/hr/employees/import,
 * POST /api/hr/imports/{leave-balances|payroll-history},
 * POST /api/accounting/accounts/import.
 * All-or-nothing by default; partial: true imports the valid rows and
 * reports the refused ones { line, reason }.
 */
export function useCsvImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      entity,
      payload,
    }: {
      entity: ImportEntity;
      payload: CsvImportPayload;
    }) =>
      // One key per submission: a retried request whose response was lost
      // gets the first answer instead of importing again.
      apiClient.post<ImportReport>(IMPORT_PATHS[entity], payload, {
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: (report, { entity }) => {
      if (!report.dryRun && report.imported > 0) {
        // Listes de l'entité importée + checklist de l'assistant.
        if (entity === 'clients') {
          queryClient.invalidateQueries({ queryKey: clientKeys.all });
        } else if (entity === 'articles') {
          queryClient.invalidateQueries({ queryKey: articleKeys.all });
        } else if (entity === 'employees') {
          queryClient.invalidateQueries({ queryKey: hrKeys.employees() });
        } else if (entity === 'leave-balances') {
          queryClient.invalidateQueries({ queryKey: hrKeys.leaves() });
          queryClient.invalidateQueries({ queryKey: hrImportBatchKeys.list(entity) });
        } else if (entity === 'payroll-history') {
          queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
          queryClient.invalidateQueries({ queryKey: hrImportBatchKeys.list(entity) });
        } else if (entity === 'chart-accounts') {
          queryClient.invalidateQueries({ queryKey: accountingKeys.all });
        } else if (isCrmImportKind(entity)) {
          queryClient.invalidateQueries({ queryKey: crmKeys.all });
        } else {
          queryClient.invalidateQueries({ queryKey: supplierKeys.all });
        }
        if (isEntityImportKind(entity)) {
          queryClient.invalidateQueries({ queryKey: entityImportBatchKeys.list(entity) });
        }
        queryClient.invalidateQueries({ queryKey: onboardingKeys.state() });
      }
    },
  });
}

/**
 * Import de la balance d'ouverture —
 * POST /api/onboarding/import/opening-balance
 * UNE écriture équilibrée au journal AN (sourceType OPENING) —
 * toujours tout-ou-rien, idempotente par exercice (409 si déjà faite).
 */
export function useImportOpeningBalance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OpeningBalancePayload) =>
      apiClient.post<OpeningBalanceReport>('/onboarding/import/opening-balance', payload, {
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: (report) => {
      if (report.entry !== null) {
        // Écritures, balances et grand livre sont impactés par l'AN.
        queryClient.invalidateQueries({ queryKey: accountingKeys.all });
        queryClient.invalidateQueries({ queryKey: accountingOpsKeys.all });
        queryClient.invalidateQueries({ queryKey: onboardingKeys.state() });
      }
    },
  });
}

/**
 * Cancels the imported à-nouveau of an exercise —
 * POST /api/onboarding/import/opening-balance/cancel (accounting:post).
 * The entry is reversed, never deleted, and the exercise takes a new import;
 * entries, balances, ledger and the checklist move with it.
 */
export function useCancelImportedOpening() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CancelImportedOpeningPayload) =>
      apiClient.post<CancelImportedOpeningResult>(
        '/onboarding/import/opening-balance/cancel',
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.all });
      queryClient.invalidateQueries({ queryKey: accountingOpsKeys.all });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.state() });
    },
  });
}

/**
 * HR import batches still present — GET /api/hr/imports/:kind/batches
 * (hr:write, plus payroll:run for payroll-history; plan feature hr).
 */
export function useHrImportBatches(kind: HrImportKind, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: hrImportBatchKeys.list(kind),
    queryFn: () =>
      apiClient.get<{ batches: HrImportBatch[] }>(`/hr/imports/${kind}/batches`),
    enabled: options.enabled ?? true,
  });
}

/**
 * Cancels an HR import batch — POST /api/hr/imports/:kind/batches/:batchId/cancel
 * (hr:write, plus payroll:run for payroll-history; reason required). Its rows are
 * removed and the cancellation is audited.
 */
export function useCancelHrImportBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      kind,
      batchId,
      reason,
    }: {
      kind: HrImportKind;
      batchId: string;
      reason: string;
    }) =>
      apiClient.post<CancelHrImportResult>(`/hr/imports/${kind}/batches/${batchId}/cancel`, {
        reason,
      }),
    onSuccess: (_result, { kind }) => {
      queryClient.invalidateQueries({ queryKey: hrImportBatchKeys.list(kind) });
      queryClient.invalidateQueries({
        queryKey: kind === 'payroll-history' ? hrKeys.payroll() : hrKeys.leaves(),
      });
    },
  });
}

/**
 * Client, article, supplier, employee or sub-account import batches still
 * present — GET /api/onboarding/import/{clients|articles|suppliers}/batches,
 * GET /api/hr/imports/employees/batches,
 * GET /api/accounting/accounts/import/batches.
 */
export function useEntityImportBatches(
  kind: EntityImportKind,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: entityImportBatchKeys.list(kind),
    queryFn: () =>
      apiClient.get<{ batches: EntityImportBatch[] }>(ENTITY_IMPORT_BATCH_PATHS[kind]),
    enabled: options.enabled ?? true,
  });
}

/**
 * Undoes a client, article, supplier, employee or sub-account import batch —
 * POST …/batches/:batchId/cancel (reason required). Its records and their
 * auxiliary accounts are removed and the undo is audited; a record used
 * since refuses it (409 IMPORT_UTILISE, details.blockers).
 */
export function useCancelEntityImportBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      kind,
      batchId,
      reason,
    }: {
      kind: EntityImportKind;
      batchId: string;
      reason: string;
    }) =>
      apiClient.post<CancelEntityImportResult>(
        `${ENTITY_IMPORT_BATCH_PATHS[kind]}/${batchId}/cancel`,
        { reason }
      ),
    onSuccess: (_result, { kind }) => {
      queryClient.invalidateQueries({ queryKey: entityImportBatchKeys.list(kind) });
      if (kind === 'clients') {
        queryClient.invalidateQueries({ queryKey: clientKeys.all });
      } else if (kind === 'articles') {
        queryClient.invalidateQueries({ queryKey: articleKeys.all });
      } else if (kind === 'employees') {
        queryClient.invalidateQueries({ queryKey: hrKeys.employees() });
      } else if (kind === 'chart-accounts') {
        queryClient.invalidateQueries({ queryKey: accountingKeys.all });
      } else if (isCrmImportKind(kind)) {
        queryClient.invalidateQueries({ queryKey: crmKeys.all });
      } else {
        queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      }
      if (kind !== 'articles' && kind !== 'chart-accounts' && !isCrmImportKind(kind)) {
        queryClient.invalidateQueries({ queryKey: auxiliaryKeys.all });
      }
      queryClient.invalidateQueries({ queryKey: onboardingKeys.state() });
    },
  });
}

/**
 * Import of the opening open items (encours clients / fournisseurs) —
 * POST /api/onboarding/import/open-items (accounting:write).
 * One AN entry detailing the imported opening by third party; always
 * all-or-nothing.
 */
export function useImportOpenItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OpenItemsPayload) =>
      apiClient.post<OpenItemsReport>('/onboarding/import/open-items', payload, {
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: (report) => {
      if (report.entry !== null) {
        queryClient.invalidateQueries({ queryKey: accountingKeys.all });
        queryClient.invalidateQueries({ queryKey: accountingOpsKeys.all });
        queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      }
    },
  });
}

/**
 * Cancels the imported open items of (year, side) —
 * POST /api/onboarding/import/open-items/cancel (accounting:post).
 * The entry is reversed, never deleted; a new file can then be imported.
 */
export function useCancelImportedOpenItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CancelImportedOpenItemsPayload) =>
      apiClient.post<CancelImportedOpenItemsResult>(
        '/onboarding/import/open-items/cancel',
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.all });
      queryClient.invalidateQueries({ queryKey: accountingOpsKeys.all });
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
    },
  });
}
